import { CARD_KIND_LABELS, SA_EXCEPTION_LABELS, SA_RULE_LABELS, type CardKind, type SaException, type SaRule } from '@evi/shared';
import type { StandingActionRow, StandingAuthorityView } from './standingAuthority';

/** Standing authority's lines in plain words (Warren, 29 Oct 2026), for the Settings section. Pure.
 *  Run: npx tsx --test src/lib/standingAuthorityView.test.ts (from apps/admin). */
/** "US$10.00", "C$10.00". */
export function moneyLabel(cents: number, currency: string): string {
  const prefix = currency === 'USD' ? 'US$' : currency === 'CAD' ? 'C$' : `${currency} `;
  return `${prefix}${(cents / 100).toFixed(2)}`;
}

/** Cents as a dollars field ("25.00"), and back: whole cents, never negative; anything unreadable is 0. */
export const centsToDollars = (cents: number) => (cents / 100).toFixed(2);
export function dollarsToCents(text: string): number {
  const n = Number(text.replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
}

const STATUS_WORDS: Record<string, string> = {
  done: 'done', would_have: 'would have done (shadow)', to_person: 'sent to a person', refused: 'refused', failed: 'failed', unknown: 'outcome unknown',
  reserved: 'reserved', applying: 'running',
};

/** One ledger row as a line: what, for whom, how much, and what happened. */
export function standingActionLine(r: StandingActionRow): string {
  const what = CARD_KIND_LABELS[r.kind as CardKind] ?? r.kind;
  const rule = SA_RULE_LABELS[r.rule as SaRule] ?? r.rule;
  const amount = r.money ? `, ${moneyLabel(r.money.cents, r.money.currency)}` : '';
  const who = r.driver ? ` for ${r.driver}` : '';
  const why = r.reason ? `: ${SA_EXCEPTION_LABELS[r.reason as SaException] ?? r.reason}` : '';
  return `${what}${who}${amount} (${rule}), ${STATUS_WORDS[r.status] ?? r.status}${why}.`;
}

/** The month line under the cap: USD and CAD apart, the total at par, what is left. */
export function monthLine(m: StandingAuthorityView['month']): string {
  const usd = m.by_currency['USD'] ?? 0, cad = m.by_currency['CAD'] ?? 0;
  return `This month: ${moneyLabel(usd, 'USD')} and ${moneyLabel(cad, 'CAD')}, ${moneyLabel(m.used_cents, 'USD')} at par of ${moneyLabel(m.cap_cents, 'USD')}`
    + `${m.person_cents ? ` (${moneyLabel(m.person_cents, 'USD')} approved by a person)` : ''}; ${moneyLabel(m.remaining_cents, 'USD')} left.`;
}
